import type { IPriceCard, PriceCardRate } from '../../../redux/buzzbridge/types';

export type RateCardPlatformId = 'instagram' | 'tiktok' | 'youtube';

export type PriceCardContentKey = keyof IPriceCard;

export interface RateCardDeliverableConfig {
    key: PriceCardContentKey;
    title: string;
    description: string;
    marketTip: string;
    icon: 'movie' | 'history' | 'image' | 'smart_display' | 'play_circle';
}

export const RATE_CARD_PLATFORMS: {
    id: RateCardPlatformId;
    label: string;
    icon: 'photo_camera' | 'play_circle' | 'smart_display';
}[] = [
    { id: 'instagram', label: 'Instagram', icon: 'photo_camera' },
    { id: 'tiktok', label: 'TikTok', icon: 'play_circle' },
    { id: 'youtube', label: 'YouTube', icon: 'smart_display' },
];

export const PLATFORM_DELIVERABLES: Record<RateCardPlatformId, RateCardDeliverableConfig[]> = {
    instagram: [
        {
            key: 'reel',
            title: 'Reel',
            description:
                'Full-screen vertical video with high discoverability. Includes original audio and basic editing.',
            marketTip: 'Creators with your reach often charge 15% more for Reels than static posts.',
            icon: 'movie',
        },
        {
            key: 'story',
            title: 'Story',
            description: '24-hour temporary content, ideal for direct call-to-actions and high engagement links.',
            marketTip: 'Link stickers increase value by about 10% on average.',
            icon: 'history',
        },
        {
            key: 'post',
            title: 'Static post',
            description: 'Evergreen grid placement — single image or carousel to maintain feed presence.',
            marketTip: 'Carousels (3+ images) typically command a $50 premium.',
            icon: 'image',
        },
    ],
    tiktok: [
        {
            key: 'video',
            title: 'Video',
            description: 'Short-form vertical video optimized for TikTok discovery and native trends.',
            marketTip: 'Trending audio and hooks can justify a 10–20% premium at your tier.',
            icon: 'play_circle',
        },
        {
            key: 'live stream',
            title: 'Live stream',
            description: 'Real-time session with audience interaction — strong for launches and Q&A.',
            marketTip: 'Live rates are often 1.5× your standard video fee.',
            icon: 'smart_display',
        },
    ],
    youtube: [
        {
            key: 'video',
            title: 'Long-form video',
            description: 'Dedicated integration or full video with brand messaging and description links.',
            marketTip: 'Mid-roll mentions typically add 15% to base video pricing.',
            icon: 'smart_display',
        },
        {
            key: 'reel',
            title: 'Short',
            description: 'YouTube Shorts — vertical, under 60 seconds, optimized for the Shorts feed.',
            marketTip: 'Shorts are often priced at 40–60% of your long-form video rate.',
            icon: 'movie',
        },
    ],
};

export function platformTileKeyToId(tileKey: string): RateCardPlatformId | null {
    if (tileKey === 'ig') return 'instagram';
    if (tileKey === 'tt') return 'tiktok';
    if (tileKey === 'yt') return 'youtube';
    return null;
}

export function getStoredDeliverableRate(pricing: Partial<IPriceCard> | undefined, key: PriceCardContentKey): number {
    const raw = pricing?.[key] as number | [number] | [number, number] | undefined;
    if (raw == null) return 0;
    if (Array.isArray(raw)) {
        if (raw.length >= 2) return Math.round((Number(raw[0]) + Number(raw[1])) / 2);
        return Number(raw[0]) || 0;
    }
    return Number(raw) || 0;
}

/** Persist single-dollar amount as [min, max] for API compatibility with legacy sliders. */
export function rateToPricingTuple(amount: number): PriceCardRate {
    const v = Math.max(0, Math.round(amount));
    return [v, v];
}
